import { useContext, useMemo } from "react";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { toast, Modal, Box, ModalContext, Text, ViewerContext } from "dash-ui";

import {
  SubscriptionProduct,
  useChangeCurrentProductSubscriptionMutation,
} from "gql/generated.graphql";
import { usePaymentConfirmation } from "common/hooks/use-payment-confirmation/use-payment-confirmation";

import { getModalConfirmationTextLabels } from "./SubscriptionChangeConfirmationModal.helpers";
import { GTMEventsEnum } from "common/google-tag-manager/constants";
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from "common/google-tag-manager/helpers";
import { useNavigate } from "react-router-dom";
import { RoutesPath } from "flow-components/routes/routes";
import { RecurringTypesEnum, SubscriptionProductModel } from "dash-common";

const SubscriptionChangeConfirmationModal = () => {
  const navigate = useNavigate();
  const { account } = useContext(ViewerContext);
  const { isOpenCurrent, payload, toggleModal } = useContext(ModalContext);
  const isOpen = !!isOpenCurrent["SubscriptionChangeConfirmationModal"];
  const { t } = useTranslation(["common", "pages"]);
  const { t: billingPageLabels } = useTranslation("pages", {
    keyPrefix: "profilePage.billing",
  });
  const theme = useTheme();
  const { subscription } = useContext(ViewerContext);
  const closeModal = () => {
    toggleModal(false, "SubscriptionChangeConfirmationModal");
  };

  const oldProduct = subscription?.subscriptionProduct as SubscriptionProductModel;
  const newProduct = payload?.newProduct;

  const shouldRedirectToAddPayment = useMemo(
    () => newProduct?.price !== 0,
    [oldProduct, newProduct]
  );
  const onChangeSuccessful = () => {
    closeModal();
    toast.success(billingPageLabels("subscriptionProductChanged"));
    // remove notification timestamp from local storage to show notification again
    localStorage.removeItem("notificationDismissTimestamp");
  };

  const [changeProductSubscription, { loading }] =
    useChangeCurrentProductSubscriptionMutation();

  const [paymentConfirmation, { stripeLoading }] = usePaymentConfirmation({
    onSuccess: onChangeSuccessful,
    onError: closeModal,
  });

  const { buttonLabel, titleLabel, descriptionLabel } =
    getModalConfirmationTextLabels({ oldProduct, newProduct }, { t });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      title={titleLabel}
      cancelButton={{ title: t("common:labels.cancel"), onClick: closeModal }}
      submitButton={{
        title: buttonLabel,
        isLoading: loading || stripeLoading,
        disabled: loading || stripeLoading,
        colorVariation: "default",
        onClick: () => {
          if (shouldRedirectToAddPayment) {
            navigate(
              RoutesPath.AddPayment.replace(":subscriptionId", newProduct._id)
            );
          } else {
            changeProductSubscription({
              variables: { input: { subscriptionProductId: newProduct._id } },
            })
              .then(({ data }) => {
                paymentConfirmation(data?.changeCurrentProductSubscription);
                toggleModal(false, "UpgradeSubscriptionModal");
              })
              .catch((error) => toast.error(error.toString()));
          }
        },
      }}>
      <Box py={3}>
        <Text color={theme.colors.n80}>{descriptionLabel}</Text>
      </Box>
    </Modal>
  );
};

export default SubscriptionChangeConfirmationModal;
